import { IconFont, SignTitle, UpLoadFile } from '@/components'
import { Message } from '@/components/message'
import { Form, Input, Modal, Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useCreateOfflineTaskResult, useImportOfflineResult } from './utils'
import { t } from '@/i18n'

const { useForm } = Form
const { Link } = Typography

interface PropsType {
  open: boolean
  initTableData: () => void
  onCancel: () => void
}

const ImportModal: React.FC<PropsType> = ({ open, initTableData, onCancel }) => {
  const [form] = useForm()
  // 导入离线任务结果
  const [importOfflineResult] = useImportOfflineResult()
  // 创建离线任务结果
  const [createOfflineTaskResult, loading] = useCreateOfflineTaskResult()
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [fileId, setFileId] = useState('')
  const [fileName, setFileName] = useState('')
  const [taskInfo, setTaskInfo] = useState<{ taskId: string; taskName: string }>()

  const handleCancel = () => {
    setUploadLoading(false)
    setUploadSuccess(false)
    setFileId('')
    setFileName('')
    setTaskInfo(undefined)
    form.resetFields()
    onCancel()
  }

  const onFinish = async (values: any) => {
    if (!fileId) return Message.error(t('请选择离线验证任务结果文件'))
    if (taskInfo?.taskId) {
      const res = await createOfflineTaskResult(taskInfo?.taskId, {
        fileId,
        taskResultName: values.taskResultName,
      })

      const { taskResultId } = res.data
      if (taskResultId) {
        Message.success(t('创建离线任务成功'))
        handleCancel()
        initTableData()
      }
    }
  }

  useEffect(
    () => () => {
      handleCancel()
    },
    [],
  )

  return (
    <ModalWrapper
      width={500}
      title={<SignTitle show>{t('导入离线验证任务结果')}</SignTitle>}
      open={open}
      onCancel={handleCancel}
      centered
      onOk={form.submit}
      loadding={loading}
    >
      {uploadSuccess ? (
        <ResultContainer>
          <div className="top">
            <IconFont type={'icon-chenggong2'} className="icon" />
            <div className="result">{t('上传成功')}</div>
          </div>
          <div className="tip" style={{ width: '100%', justifyContent: 'flex-start' }}>
            <span style={{ color: '#999' }}>{t('请选择离线验证任务结果文件')}</span>
          </div>
          <div className="bottom">
            <div className="item">
              <div className="label">{t('文件名：')}</div>
              <div className="value">{fileName}</div>
            </div>
            <div className="item">
              <span className="label">{t('关联主任务：')}</span>
              <div className="value">{taskInfo?.taskName}</div>
            </div>
          </div>
        </ResultContainer>
      ) : (
        <>
          <UpLoadFile
            uploadXlsxLoading={uploadLoading}
            showUploadList={false}
            disabled={uploadLoading}
            headers={{
              'user-id': JSON.parse(localStorage.getItem('token') as string).id,
              token: JSON.parse(localStorage.getItem('token') as string).token,
            }}
            action={`${
              process.env.NODE_ENV === 'dev' ? '/api/portal/oss/upload' : '/portal/oss/upload'
            }`}
            beforeUpload={file => {
              const maxSize = 100 * 1024 * 1024
              if (file.size > maxSize) {
                Message.error(t('导入失败，文件大小超出上限'))
                return false
              }
              setFileName(file.name)
              return true
            }}
            progress={{
              strokeColor: {
                '0%': '#108ee9',
                '100%': '#87d068',
              },
              strokeWidth: 3,
              format: percent => percent && `${parseFloat(percent.toFixed(2))}%`,
            }}
            onChange={({ file }) => {
              if (file.status === 'uploading') {
                setUploadLoading(true)
                return
              }

              if (
                file.status === 'done' ||
                file.status === 'error' ||
                file.status === 'removed' ||
                file.status === 'success'
              ) {
                setUploadLoading(false)
              }
              if (file.status === 'done') {
                const { file_id } = file.response.data
                setFileId(file_id)
                importOfflineResult({ fileId: file_id })
                  .then(res => {
                    setUploadSuccess(true)
                    setTaskInfo(res.data)
                    form.setFieldsValue({ taskResultName: res.data.taskName })
                    Message.success(t('上传成功'))
                  })
                  .catch(_ => {})
                return
              }

              if (file.status === 'error') {
                Message.error(t('上传失败'))
              }
            }}
            name={'file'}
            maxCount={1}
            text={
              uploadLoading ? (
                t('上传中,请稍后')
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <div style={{ color: '#666666', marginRight: '8px' }}>
                    {t('将文件拖拽到此处，或')}
                    <Link style={{ color: '#4238c9', borderBottom: '1px solid #4238c9' }}>
                      {t('点击上传')}
                    </Link>
                  </div>
                  <div style={{ color: '#666666' }}>{t('(仅支持.db)')}</div>
                </div>
              )
            }
          />

          <div className="tip">
            <span style={{ color: '#999' }}>{t('请选择离线验证任务结果文件')}</span>
          </div>
        </>
      )}
      <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: 10 }}>
        <Form.Item
          label={t('子任务名称')}
          name="taskResultName"
          rules={[
            { required: true, message: t('请输入子任务名称') },
            { max: 100, message: t('子任务名称最多99字') },
          ]}
        >
          <Input placeholder={t('请输入')} />
        </Form.Item>
      </Form>
    </ModalWrapper>
  )
}

export default ImportModal

const ModalWrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }
    .ant-modal-body {
      padding-bottom: 0;
    }

    .ant-upload {
      height: 196px;
    }

    .tip {
      margin-top: 10px;
      margin-bottom: 16px;

      span {
        color: #999;
      }
    }
  }
`

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .top {
    width: 436px;
    height: 196px;
    margin-bottom: 10px;
    background-color: #f7f8f9;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .bottom {
    display: flex;
    flex-direction: column;

    .item {
      width: 436px;
      height: 36px;
      background: #f7f8fa;
      border-radius: 8px;
      margin-bottom: 8px;
      line-height: 36px;
      padding-left: 12px;
      display: flex;

      .label {
        min-width: 84px;
        margin-right: 4px;
        font-size: 14px;
        color: #86909c;
      }

      .value {
      }
    }
  }

  .icon {
    font-size: 54px;
  }

  .result {
    margin-top: 16px;
    font-size: 16px;
    font-weight: 500;
  }

  .text {
    color: #666;
    line-height: 1.2;
  }

  .content-container {
    width: 100%;
    display: flex;
    justify-content: space-around;
  }

  .content {
    width: 168px;
    height: 152px;
    background: #f7f8f9;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;

    div {
      margin-bottom: 12px;
    }
  }

  .num {
    font-weight: bold;
    font-size: 18px;
    color: #558492;
  }

  .num-err {
    font-weight: bold;
    font-size: 18px;
    color: #f53f3f;
  }
`
